import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { BASE_URL } from '../context/api'; // Ensure this is correctly defined
import { format } from 'date-fns'; // For formatting dates
import AuthContext from '../context/AuthContext'; // Import AuthContext
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Loader = () => (
  <div className="flex justify-center items-center">
    <div className="relative">
      <div className="w-12 h-12 rounded-full absolute border-4 border-pink-200"></div>
      <div className="w-12 h-12 rounded-full animate-spin absolute border-4 border-pink-600 border-t-transparent"></div>
    </div>
  </div>
);

const TempPayments = () => {
    const [payments, setPayments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchParams, setSearchParams] = useState({
        reg_number: '',
        term: '',
        year: '',
        paynow_reference: ''
    });
    const [pagination, setPagination] = useState({
        currentPage: 1,
        totalPages: 1
    });
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const pageSize = 100;

    const { username } = useContext(AuthContext); // Get the username from AuthContext

    const allowedToEdit = ['sysadmin', 'financeadmin'].includes(username);

    useEffect(() => {
        fetchPayments();
    }, [pagination.currentPage]);

    const fetchPayments = async () => {
        setLoading(true);
        setError('');

        try {
            let endpoint = `${BASE_URL}/temp-payments/search`;
            let params = {
                page: pagination.currentPage,
                pageSize
            };

            const hasSearchParams = Object.values(searchParams).some(param => param !== '');

            if (hasSearchParams) {
                Object.entries(searchParams).forEach(([key, value]) => {
                    if (value !== '') {
                        params[key] = value;
                    }
                });
            }

            const response = await axios.get(endpoint, { params });

            setPayments(response.data.payments);
            setPagination({
                currentPage: response.data.currentPage,
                totalPages: response.data.totalPages
            });
        } catch (err) {
            console.error('Error fetching payments:', err);
            setError('Failed to fetch payments.');
        } finally {
            setLoading(false);
        }
    };

    const handleSearchChange = (e) => {
        const { name, value } = e.target;
        setSearchParams(prevParams => ({
            ...prevParams,
            [name]: value
        }));
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPagination(prev => ({
            ...prev,
            currentPage: 1
        }));
        fetchPayments();
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({
                ...prev,
                currentPage: newPage
            }));
        }
    };

    const openModal = (payment) => {
        setSelectedPayment(payment);
        setShowModal(true);
    };

    const closeModal = () => {
        setSelectedPayment(null);
        setShowModal(false);
    };

    const openApproveModal = (payment) => {
        setSelectedPayment(payment);
        setShowApproveModal(true);
    };

    const closeApproveModal = () => {
        setSelectedPayment(null);
        setShowApproveModal(false);
    };

    const handleApprovePayment = async () => {
        try {
            await axios.post(`${BASE_URL}/edit-payments/temp-payments/finalize`, {
                tempPaymentId: selectedPayment.id,
                sourceTable: selectedPayment.sourceTable,
            });
            toast.success('Payment approved and marked as Paid.');
            fetchPayments(); // Refresh the payments list
            closeApproveModal();
        } catch (error) {
            console.log(error);
            toast.error('Failed to approve payment.');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4">
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-gray-800">Failed or Pending Payments</h1>
                        <div className="h-1 w-full bg-gray-200 mt-2"></div>
                    </div>

                    {/* Search Form */}
                    <div className="mb-6 p-6 bg-gray-50 rounded-lg border border-gray-200">
                        <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Registration Number</label>
                                <input
                                    type="text"
                                    name="reg_number"
                                    value={searchParams.reg_number}
                                    onChange={handleSearchChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                    placeholder="e.g., R76539A"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Term</label>
                                <select
                                    name="term"
                                    value={searchParams.term}
                                    onChange={handleSearchChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                >
                                    <option value="">All Terms</option>
                                    <option value="1">Term 1</option>
                                    <option value="2">Term 2</option>
                                    <option value="3">Term 3</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                                <input
                                    type="number"
                                    name="year"
                                    value={searchParams.year}
                                    onChange={handleSearchChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                    placeholder="e.g., 2024"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Transaction Reference</label>
                                <input
                                    type="text"
                                    name="paynow_reference"
                                    value={searchParams.paynow_reference}
                                    onChange={handleSearchChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                                    placeholder="Reference"
                                />
                            </div>
                            <div className="md:col-span-4 flex gap-2">
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 text-pink-600 hover:text-pink-900 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                                >
                                    Search
                                </button>
                                <button
                                    type="button"
                                    className="flex-1 px-4 py-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                    onClick={() => {
                                        setSearchParams({
                                            reg_number: '',
                                            term: '',
                                            year: '',
                                            paynow_reference: ''
                                        });
                                        setPagination(prev => ({
                                            ...prev,
                                            currentPage: 1
                                        }));
                                        fetchPayments();
                                    }}
                                >
                                    Reset
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-red-700">{error}</p>
                        </div>
                    )}

                    {/* Table Section */}
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                        {loading ? (
                            <div className="flex justify-center items-center py-12">
                                <Loader />
                            </div>
                        ) : (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-pink-600 rounded-t-lg">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider rounded-tl-lg">Date</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Reg Number</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Txn Ref</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Received Amount</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Reported Amount</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Currency</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Status</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider rounded-tr-lg">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {payments.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" className="px-6 py-4 text-center text-gray-500">No temporary payments found.</td>
                                        </tr>
                                    ) : (
                                        payments.map((payment) => (
                                            <tr key={payment.id} className="hover:bg-pink-50">
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{format(new Date(payment.created_at), 'yyyy-MM-dd HH:mm')}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.reg_number}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.paynow_reference}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.received_amount}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.reported_amount}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.currency}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.status}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                                    <div className="flex gap-2">
                                                        <button
                                                            className="text-pink-600 hover:text-pink-900 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                                                            onClick={() => openModal(payment)}
                                                        >
                                                            View Details
                                                        </button>
                                                        {allowedToEdit && (
                                                            <button
                                                                className="text-green-600 hover:text-green-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                                                onClick={() => openApproveModal(payment)}
                                                            >
                                                                Approve
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* Pagination Controls */}
                    <div className="mt-6 flex justify-between items-center">
                        <button
                            className="px-4 py-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={pagination.currentPage === 1}
                        >
                            Previous
                        </button>
                        <span className="text-sm text-gray-600">
                            Page {pagination.currentPage} of {pagination.totalPages}
                        </span>
                        <button
                            className="px-4 py-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={pagination.currentPage === pagination.totalPages}
                        >
                            Next
                        </button>
                    </div>

                    {/* Modal for Payment Details */}
                    {showModal && selectedPayment && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4">
                                <div className="p-6">
                                    <h3 className="text-lg font-bold text-gray-800 mb-4">Payment Details</h3>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <p className="text-sm text-gray-500">Reg Number</p>
                                            <p className="text-gray-900">{selectedPayment.reg_number}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Form</p>
                                            <p className="text-gray-900">{selectedPayment.form}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Class Type</p>
                                            <p className="text-gray-900">{selectedPayment.class_type}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Term</p>
                                            <p className="text-gray-900">{selectedPayment.term}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Year</p>
                                            <p className="text-gray-900">{selectedPayment.year}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Payment Method</p>
                                            <p className="text-gray-900">{selectedPayment.payment_method}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Status</p>
                                            <p className="text-gray-900">{selectedPayment.status}</p>
                                        </div>
                                        <div className="col-span-2">
                                            <p className="text-sm text-gray-500">Txn Reference</p>
                                            <p className="text-gray-900">{selectedPayment.paynow_reference}</p>
                                        </div>
                                        <div className="col-span-2">
                                            <p className="text-sm text-gray-500">Created At</p>
                                            <p className="text-gray-900">{format(new Date(selectedPayment.created_at), 'yyyy-MM-dd HH:mm')}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Received Amount</p>
                                            <p className="text-gray-900">{selectedPayment.received_amount}</p>
                                        </div>
                                        <div>
                                            <p className="text-sm text-gray-500">Reported Amount</p>
                                            <p className="text-gray-900">{selectedPayment.reported_amount}</p>
                                        </div>
                                    </div>
                                    <div className="mt-6 flex justify-end">
                                        <button
                                            className="px-4 py-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                            onClick={closeModal}
                                        >
                                            Close
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Modal for Approving Payment */}
                    {showApproveModal && selectedPayment && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                            <div className="bg-white rounded-lg shadow-lg max-w-lg w-full mx-4">
                                <div className="p-6">
                                    <h3 className="text-lg font-bold text-gray-800 mb-4">Approve Payment</h3>
                                    <p className="text-gray-600 mb-4">
                                        You are about to approve this payment. Once approved, the payment will be validated and marked as paid in the system. 
                                        Please ensure you have verified all details before proceeding.
                                    </p>
                                    
                                    <div className="mt-6 flex justify-end gap-2">
                                        <button
                                            className="px-4 py-2 text-gray-600 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                            onClick={closeApproveModal}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2"
                                            onClick={handleApprovePayment}
                                        >
                                            Yes, Approve
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <ToastContainer />
                </div>
            </div>
        </div>
    );
};

export default TempPayments;
